import { type PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import { componenteDidatico } from '../../dominio/didatico/catalogo'
import { modulosSrs, type GeradorSrs, type ModuloSrs } from '../../dominio/didatico/pontosSrs'
import { CORES_SEMANTICAS } from '../../dominio/didatico/tokens'
import { CaixaArredondada } from '../componentes/CaixaArredondada'
import { MaterialPeca, Peca } from '../componentes/Peca'
import { Rotulo3D } from '../componentes/Rotulo3D'
import { useMaquete } from '../componentes/contextoMaquete'

/** tecido de poliamida das bolsas, que sai branco-acinzentado do módulo */
const COR_TECIDO = '#e9e6de'
/** comprimento de cada câmara da cortina */
const CAMARA_CORTINA_M = 0.15

/**
 * Módulos SRS do perfil didático.
 *
 * Só aparece o que o perfil declara (ver `modulosSrs`). A maquete é
 * configurável exatamente para não sugerir que todo veículo tenha todos os
 * dispositivos, nem nestas posições.
 */
export function SistemaSRS({ perfil }: { perfil: PerfilVeiculo }) {
  const { mostrarRotulos, camadas, expansaoAirbag, srsAcionados } = useMaquete()
  const presentes = modulosSrs(perfil)
  const zCentral = perfil.colunaA.zBase - 0.18
  const yCentral = perfil.pisoY + 0.08
  const mxLateral = perfil.largura / 2 - 0.06
  const mxDianteiro = perfil.largura * 0.28
  const yDianteiro = perfil.topoSoleiraY + 0.12
  const zDianteiro = perfil.frenteZ - 0.22

  return (
    <group>
      {presentes.map((modulo) => (
        <ModuloSrs
          key={modulo.chave}
          modulo={modulo}
          // só expande o que ESTE tipo de colisão aciona: o módulo continua
          // desenhado, fechado, para o aluno ver que ele existe e não disparou
          expansao={srsAcionados.includes(modulo.chave) ? expansaoAirbag : 0}
          mostrarRotulo={mostrarRotulos && camadas.srs}
        />
      ))}
      {presentes.map((modulo) => (
        <GeradorGas
          key={modulo.gerador.id}
          gerador={modulo.gerador}
          emEvidencia={camadas.srs}
          mostrarRotulo={mostrarRotulos && camadas.srs}
        />
      ))}

      {/* Módulo / Central Eletrônica do SRS no túnel central */}
      <Peca id="centralSrs" deslocamentoAfastado={[0, 0.2, 0]}>
        <mesh position={[0, yCentral, zCentral]}>
          <CaixaArredondada args={[0.13, 0.045, 0.12]} />
          <MaterialPeca id="centralSrs" corBase="#d97706" metalico={0.65} rugosidade={0.35} />
        </mesh>
        {mostrarRotulos && camadas.srs && (
          <Rotulo3D posicao={[0, yCentral + 0.12, zCentral]} texto="CENTRAL SRS" variante="srs" fator={3.4} />
        )}
      </Peca>

      {/* Sensores de impacto dianteiros */}
      {[
        { sinal: 1, id: 'sensorImpactoDianteiroEsquerdo', rotulo: 'SENSOR D' },
        { sinal: -1, id: 'sensorImpactoDianteiroDireito', rotulo: 'SENSOR D' },
      ].map(({ sinal, id, rotulo }) => (
        <Peca key={id} id={id} deslocamentoAfastado={[0, 0, 0.35]}>
          <mesh position={[sinal * mxDianteiro, yDianteiro, zDianteiro]}>
            <CaixaArredondada args={[0.045, 0.04, 0.045]} />
            <MaterialPeca id={id} corBase="#eab308" metalico={0.5} rugosidade={0.4} />
          </mesh>
          {mostrarRotulos && camadas.srs && (
            <Rotulo3D posicao={[sinal * mxDianteiro, yDianteiro + 0.1, zDianteiro]} texto={rotulo} variante="srs" fator={3.0} />
          )}
        </Peca>
      ))}

      {/* Sensores de impacto lateral nas colunas B / soleiras */}
      {[
        { sinal: 1, id: 'sensorImpactoLateralEsquerdo' },
        { sinal: -1, id: 'sensorImpactoLateralDireito' },
      ].map(({ sinal, id }) => (
        <Peca key={id} id={id} deslocamentoAfastado={[sinal * 0.3, 0, 0]}>
          <mesh position={[sinal * mxLateral, perfil.topoSoleiraY + 0.06, perfil.colunaB.z]}>
            <CaixaArredondada args={[0.035, 0.05, 0.04]} />
            <MaterialPeca id={id} corBase="#eab308" metalico={0.5} rugosidade={0.4} />
          </mesh>
        </Peca>
      ))}
    </group>
  )
}

function ModuloSrs({
  modulo,
  expansao,
  mostrarRotulo,
}: {
  modulo: ModuloSrs
  expansao: number
  mostrarRotulo: boolean
}) {
  const id = modulo.chave
  const { x, y, z } = modulo.posicao

  return (
    <Peca id={id}>
      {/* tampa do módulo: é por ela que a bolsa rasga ao abrir */}
      <mesh position={[x, y, z]}>
        <CaixaArredondada args={[...modulo.dimensoesModulo]} raio={0.012} />
        <MaterialPeca id={id} metalico={0.2} rugosidade={0.6} />
      </mesh>

      {expansao > 0.02 && (modulo.bolsa.forma === 'cortina'
        ? <BolsaCortina id={id} modulo={modulo} expansao={expansao} />
        : <BolsaAlmofada id={id} modulo={modulo} expansao={expansao} />)}

      {mostrarRotulo && <RotuloSrs id={id} posicao={[x, y + 0.16, z]} />}
    </Peca>
  )
}

/**
 * Bolsa frontal, lateral, de banco ou de coluna: elipsoide achatado que cresce
 * do módulo até a posição inflada, com a costura do perímetro.
 */
function BolsaAlmofada({ id, modulo, expansao }: { id: string; modulo: ModuloSrs; expansao: number }) {
  const { bolsa, posicao } = modulo
  const e = Math.min(1, expansao)
  const centro: [number, number, number] = [
    posicao.x + (bolsa.centro.x - posicao.x) * e,
    posicao.y + (bolsa.centro.y - posicao.y) * e,
    posicao.z + (bolsa.centro.z - posicao.z) * e,
  ]
  const [lx, ly, lz] = bolsa.dimensoes
  // a bolsa abre primeiro em área e só depois ganha volume
  const escala: [number, number, number] = [
    Math.max(0.01, (lx / 2) * Math.sqrt(e)),
    Math.max(0.01, (ly / 2) * Math.sqrt(e)),
    Math.max(0.01, (lz / 2) * e),
  ]
  // a costura corre no plano da maior face: xy nas frontais, yz nas laterais
  const faceLateral = lx < lz
  return (
    <group position={centro} rotation={[bolsa.inclinacaoX, 0, 0]}>
      <mesh scale={escala}>
        <sphereGeometry args={[1, 28, 20]} />
        <MaterialPeca id={id} corBase={COR_TECIDO} metalico={0} rugosidade={0.92} />
      </mesh>
      <mesh
        rotation={faceLateral ? [0, Math.PI / 2, 0] : [0, 0, 0]}
        scale={faceLateral ? [escala[2], escala[1], 1] : [escala[0], escala[1], 1]}
      >
        <torusGeometry args={[1, 0.012 / Math.max(escala[0], escala[1], 0.05), 6, 40]} />
        <MaterialPeca id={id} corBase="#cfcbc1" metalico={0} rugosidade={0.95} />
      </mesh>
    </group>
  )
}

/**
 * Cortina: painel de câmaras verticais que desce do trilho do teto até a linha
 * de cintura, cobrindo as janelas da coluna A à C.
 */
function BolsaCortina({ id, modulo, expansao }: { id: string; modulo: ModuloSrs; expansao: number }) {
  const { bolsa } = modulo
  const e = Math.min(1, expansao)
  const [espessura, altura, comprimento] = bolsa.dimensoes
  const yTopo = bolsa.centro.y + altura / 2
  const alturaAtual = Math.max(0.02, altura * e)
  const camaras = Math.max(3, Math.round(comprimento / CAMARA_CORTINA_M))
  const passo = comprimento / camaras
  const zInicio = bolsa.centro.z + comprimento / 2 - passo / 2
  return (
    <group position={[bolsa.centro.x, yTopo - alturaAtual / 2, 0]}>
      {Array.from({ length: camaras }, (_, i) => (
        <mesh key={i} position={[0, 0, zInicio - i * passo]}>
          <CaixaArredondada args={[espessura * Math.max(0.3, e), alturaAtual, passo - 0.008]} raio={Math.min(0.03, espessura * 0.45)} />
          <MaterialPeca id={id} corBase={COR_TECIDO} metalico={0} rugosidade={0.92} />
        </mesh>
      ))}
    </group>
  )
}

const ROTACAO_DO_EIXO: Record<GeradorSrs['eixo'], [number, number, number]> = {
  x: [0, 0, Math.PI / 2],
  y: [0, 0, 0],
  z: [Math.PI / 2, 0, 0],
}

/**
 * Gerador de gás. Com a camada SRS ligada vira a cor de perigo, com halo da
 * zona de exclusão de corte e o rótulo GÁS: é onde o socorrista não corta.
 */
function GeradorGas({ gerador, emEvidencia, mostrarRotulo }: {
  gerador: GeradorSrs
  emEvidencia: boolean
  mostrarRotulo: boolean
}) {
  const { x, y, z } = gerador.posicao
  const cor = emEvidencia ? CORES_SEMANTICAS.geradorGas : '#8b929b'
  return (
    <Peca id={gerador.id}>
      <mesh position={[x, y, z]} rotation={ROTACAO_DO_EIXO[gerador.eixo]}>
        <cylinderGeometry args={[gerador.raio, gerador.raio, gerador.comprimento, 18]} />
        <MaterialPeca id={gerador.id} corBase={cor} metalico={0.6} rugosidade={0.35} />
      </mesh>
      {/* conector de ignição, sempre amarelo como o chicote do SRS */}
      <mesh position={[x, y, z]} rotation={ROTACAO_DO_EIXO[gerador.eixo]}>
        <cylinderGeometry args={[gerador.raio * 0.55, gerador.raio * 0.55, gerador.comprimento + 0.024, 10]} />
        <MaterialPeca id={gerador.id} corBase="#eab308" metalico={0.3} rugosidade={0.5} />
      </mesh>
      {emEvidencia && (
        <mesh position={[x, y, z]}>
          <sphereGeometry args={[Math.max(0.07, gerador.comprimento * 0.7), 18, 14]} />
          <meshBasicMaterial color={CORES_SEMANTICAS.geradorGas} transparent opacity={0.16} depthWrite={false} />
        </mesh>
      )}
      {mostrarRotulo && <Rotulo3D posicao={[x, y + 0.1, z]} texto="GÁS" variante="risco" fator={3.2} />}
    </Peca>
  )
}

function RotuloSrs({ id, posicao }: { id: string; posicao: [number, number, number] }) {
  const sigla = componenteDidatico(id)?.sigla
  if (!sigla) return null
  return <Rotulo3D posicao={posicao} texto={sigla} variante="srs" fator={3.6} />
}
